import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { Income } from '../types';
import type { RecurringType } from '../types';

export function useIncomes() {
  const { user } = useAuth();
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchIncomes = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('incomes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .order('date', { ascending: false });

      if (fetchError) throw fetchError;
      setIncomes((data ?? []) as Income[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incomes');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const addIncome = useCallback(
    async (payload: { source: string; amount: number; date: string; recurring: boolean; recurring_type: RecurringType }) => {
      if (!user) throw new Error('Not authenticated');
      const { error: insertError } = await supabase.from('incomes').insert({
        user_id: user.id,
        source: payload.source,
        amount: payload.amount,
        date: payload.date,
        recurring: payload.recurring,
        recurring_type: payload.recurring ? payload.recurring_type : 'none',
      });
      if (insertError) throw insertError;
      await fetchIncomes();
    },
    [user, fetchIncomes],
  );

  const deleteIncome = useCallback(
    async (incomeId: string) => {
      const { error: deleteError } = await supabase.from('incomes').delete().eq('id', incomeId);
      if (deleteError) throw deleteError;
      await fetchIncomes();
    },
    [fetchIncomes],
  );

  const disableRecurringIncome = useCallback(
    async (incomeId: string) => {
      if (!user) throw new Error('Not authenticated');
      const { error: updateError } = await supabase
        .from('incomes')
        .update({ recurring: false, recurring_type: 'none' })
        .eq('id', incomeId)
        .eq('user_id', user.id);
      if (updateError) throw updateError;
      await fetchIncomes();
    },
    [user, fetchIncomes],
  );

  useEffect(() => {
    fetchIncomes();
  }, [fetchIncomes]);

  return {
    incomes,
    loading,
    error,
    addIncome,
    deleteIncome,
    disableRecurringIncome,
    refetch: fetchIncomes,
  };
}
